import { useState, useCallback } from "react";
import { NodeViewWrapper, type NodeViewProps } from "@tiptap/react";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/lilia-ui/select";
import { Input } from "@/components/lilia-ui/input";
import { BlockEditModal } from "../BlockEditModal";
import styles from "../blocks/EmbedBlock.module.scss";

const ENGINES = [
  { value: "latex", label: "LaTeX" },
  { value: "typst", label: "Typst" },
  { value: "html", label: "HTML" },
];

export function EmbedNodeView({ node, updateAttributes, selected, deleteNode }: NodeViewProps) {
  const engine = (node.attrs.engine as string) || "latex";
  const code = (node.attrs.code as string) || "";
  const caption = (node.attrs.caption as string) || "";
  const label = (node.attrs.label as string) || "";
  const previewImage = (node.attrs.previewImage as string) || "";
  const packages = (node.attrs.packages as string[]) || [];

  const [isOpen, setIsOpen] = useState(false);
  const [draftEngine, setDraftEngine] = useState(engine);
  const [draftCode, setDraftCode] = useState(code);
  const [draftCaption, setDraftCaption] = useState(caption);
  const [draftLabel, setDraftLabel] = useState(label);
  const [draftPreviewImage, setDraftPreviewImage] = useState(previewImage);
  const [draftPackages, setDraftPackages] = useState(packages.join(", "));

  const handleOpen = useCallback(() => {
    setDraftEngine(engine);
    setDraftCode(code);
    setDraftCaption(caption);
    setDraftLabel(label);
    setDraftPreviewImage(previewImage);
    setDraftPackages(packages.join(", "));
    setIsOpen(true);
  }, [engine, code, caption, label, previewImage, packages]);

  const handleSave = useCallback(() => {
    const parsedPackages = draftPackages
      .split(",")
      .map((p) => p.trim())
      .filter(Boolean);
    updateAttributes({
      engine: draftEngine,
      code: draftCode,
      caption: draftCaption,
      label: draftLabel,
      previewImage: draftPreviewImage,
      packages: parsedPackages,
    });
    setIsOpen(false);
  }, [draftEngine, draftCode, draftCaption, draftLabel, draftPreviewImage, draftPackages, updateAttributes]);

  const previewLines = code.split("\n").slice(0, 5).join("\n");
  const hasMoreLines = code.split("\n").length > 5;

  const engineLabel = ENGINES.find((e) => e.value === engine)?.label || engine.toUpperCase();

  return (
    <NodeViewWrapper data-type="embed-block" data-block-id={node.attrs.blockId} className={selected ? "ProseMirror-selectednode" : ""}>
      <div className={styles.embedBlock} onClick={handleOpen} title="Click to edit embed">
        <div className={styles.header}>
          <span className={styles.engineBadge}>{engineLabel}</span>
          <span className={styles.headerLabel}>Embed</span>
        </div>

        {previewImage ? (
          <div className={styles.previewImageContainer}>
            <img src={previewImage} alt={caption || "Embed preview"} className={styles.previewImage} />
          </div>
        ) : (
          <div className={styles.codePreview}>
            {code ? (
              <>
                <pre className={styles.previewCode}>{previewLines}</pre>
                {hasMoreLines && <span className={styles.moreIndicator}>... click to expand</span>}
              </>
            ) : (
              <span className={styles.emptyHint}>Click to add {engineLabel} code</span>
            )}
          </div>
        )}

        {caption && <div className={styles.caption}>{caption}</div>}
      </div>

      <BlockEditModal
        sourceNode={node}
        title={`Edit ${ENGINES.find((e) => e.value === draftEngine)?.label || draftEngine.toUpperCase()} Embed`}
        open={isOpen}
        onClose={() => setIsOpen(false)}
        onSave={handleSave}
        onDelete={() => { setIsOpen(false); deleteNode(); }}
        width={720}
      >
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Engine</label>
          <Select value={draftEngine} onValueChange={(val) => setDraftEngine(val || "latex")}>
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ENGINES.map((eng) => (
                <SelectItem key={eng.value} value={eng.value}>{eng.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Code</label>
          <textarea
            value={draftCode}
            onChange={(e) => setDraftCode(e.target.value)}
            placeholder={
              draftEngine === "latex" ? "Enter LaTeX code..." :
              draftEngine === "typst" ? "Enter Typst code..." :
              "Enter HTML code..."
            }
            rows={12}
            autoFocus
            spellCheck={false}
            style={{
              width: "100%", padding: "12px", fontSize: 13, fontFamily: "var(--lilia-font-mono, monospace)",
              border: "1px solid var(--lilia-border, #ddd)", borderRadius: 4, resize: "vertical",
              lineHeight: 1.5, tabSize: 2,
            }}
          />
        </div>

        {draftEngine === "latex" && (
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
              Packages <span style={{ fontWeight: 400, color: "var(--lilia-text-muted, #888)" }}>(comma-separated)</span>
            </label>
            <Input
              value={draftPackages}
              onChange={(e) => setDraftPackages(e.currentTarget.value)}
              placeholder="e.g., tikz, pgfplots, amsmath"
              className="w-full"
            />
          </div>
        )}

        <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
          <div style={{ flex: 1 }}>
            <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Caption</label>
            <input
              type="text"
              value={draftCaption}
              onChange={(e) => setDraftCaption(e.target.value)}
              placeholder="Caption (optional)"
              style={{ width: "100%", padding: "8px 12px", fontSize: 13, border: "1px solid var(--lilia-border, #ddd)", borderRadius: 4 }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Label</label>
            <input
              type="text"
              value={draftLabel}
              onChange={(e) => setDraftLabel(e.target.value)}
              placeholder="Label (optional)"
              style={{ width: "100%", padding: "8px 12px", fontSize: 13, border: "1px solid var(--lilia-border, #ddd)", borderRadius: 4 }}
            />
          </div>
        </div>

        <div>
          <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Preview Image URL</label>
          <input
            type="text"
            value={draftPreviewImage}
            onChange={(e) => setDraftPreviewImage(e.target.value)}
            placeholder="https://... (optional rendered preview)"
            style={{ width: "100%", padding: "8px 12px", fontSize: 13, border: "1px solid var(--lilia-border, #ddd)", borderRadius: 4 }}
          />
          {draftPreviewImage && (
            <div style={{ marginTop: 8, textAlign: "center" }}>
              <img
                src={draftPreviewImage}
                alt="Preview"
                style={{ maxWidth: "100%", maxHeight: 200, borderRadius: 4, border: "1px solid var(--lilia-border, #ddd)" }}
                onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
              />
            </div>
          )}
        </div>
      </BlockEditModal>
    </NodeViewWrapper>
  );
}
